import React from "react";
import {
  Typography,
  Paper,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import ApiIcon from "@mui/icons-material/Api";
import EngineeringIcon from "@mui/icons-material/Engineering";
import LanguageIcon from "@mui/icons-material/Language";
import StorageIcon from "@mui/icons-material/Storage";
import BoltIcon from "@mui/icons-material/Bolt";
import HubIcon from "@mui/icons-material/Hub";
import CloudIcon from "@mui/icons-material/Cloud";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import Loading from "../Common/Loading";
import color from "../../Constants/colors";
import { useServiceStatus } from "../../Query/useMonitoring";
import { ServiceEntry } from "../../Api/monitoring";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  formatBytes,
  StatCard,
  DummyBadge,
  UsageBar,
} from "./monitoringShared";

/** Pick an icon + accent colour for a service from its name/image keywords.
 *  Order matters: more specific matches come first (e.g. weaviate_db before db). */
const getServiceVisual = (
  service: string,
  image: string,
): { icon: React.ReactElement; color: string } => {
  const haystack = `${service} ${image}`.toLowerCase();
  const has = (...terms: string[]) =>
    terms.some((term) => haystack.includes(term));

  if (has("celery", "worker"))
    return { icon: <EngineeringIcon />, color: color.accent };
  if (has("backend", "gunicorn", "flask", "api"))
    return { icon: <ApiIcon />, color: color.secondary };
  if (has("client", "frontend", "nginx", "web"))
    return { icon: <LanguageIcon />, color: color.blue700 };
  if (has("redis")) return { icon: <BoltIcon />, color: color.red600 };
  if (has("weaviate", "qdrant", "vector"))
    return { icon: <HubIcon />, color: color.secondary };
  if (has("minio", "s3", "storage"))
    return { icon: <CloudIcon />, color: color.orange500 };
  if (has("proxy", "socket"))
    return { icon: <SecurityIcon />, color: color.gray600 };
  if (has("exporter", "prometheus"))
    return { icon: <SpeedIcon />, color: color.blue700 };
  if (has("postgres", "database", "db"))
    return { icon: <StorageIcon />, color: color.green700 };
  return { icon: <ViewModuleIcon />, color: color.gray500 };
};

const StateChip: React.FC<{ state: string }> = ({ state }) => {
  const running = state === "running";
  const c = running ? color.green700 : color.gray500;
  const bg = running ? color.green50 : color.gray100;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "20px",
        backgroundColor: bg,
        border: `1px solid ${c}30`,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: c,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: c,
        }}
      >
        {state || "—"}
      </Typography>
    </Box>
  );
};

const ServiceCard: React.FC<{ service: ServiceEntry }> = ({ service }) => {
  const { t } = useTranslation("monitoring");
  const cpu = service.cpu_percent;
  const mem = service.memory;
  const net = service.network;
  const visual = getServiceVisual(service.service, service.image);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "14px",
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(5,77,127,0.10)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${visual.color}14`,
                color: visual.color,
                "& svg": { fontSize: "1.25rem" },
              }}
            >
              {visual.icon}
            </Box>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "0.95rem",
                color: color.secondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {service.service}
            </Typography>
          </Box>
          <StateChip state={service.state} />
        </Box>
        <Tooltip title={service.image} arrow>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: color.gray400,
              fontFamily: "'JetBrains Mono', monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {service.image || service.name}
          </Typography>
        </Tooltip>
      </Box>

      {cpu !== null ? (
        <UsageBar
          label={t("cpuUsage")}
          percent={cpu}
          detail={`${cpu.toFixed(1)}%`}
        />
      ) : (
        <UsageBar
          label={t("cpuUsage")}
          percent={0}
          detail={t("notAvailableShort")}
        />
      )}

      {mem ? (
        <UsageBar
          label={t("memoryUsage")}
          percent={mem.usage_percent ?? 0}
          detail={`${formatBytes(mem.used_bytes)} / ${formatBytes(mem.limit_bytes)}`}
        />
      ) : (
        <UsageBar
          label={t("memoryUsage")}
          percent={0}
          detail={t("notAvailableShort")}
        />
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          pt: 0.5,
          borderTop: `1px solid ${color.gray100}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <SouthIcon sx={{ fontSize: "0.9rem", color: color.green700 }} />
          <Typography sx={{ fontSize: "0.7rem", color: color.gray500 }}>
            {net ? formatBytes(net.rx_bytes) : "—"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <NorthIcon sx={{ fontSize: "0.9rem", color: color.accent }} />
          <Typography sx={{ fontSize: "0.7rem", color: color.gray500 }}>
            {net ? formatBytes(net.tx_bytes) : "—"}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const DockerServicesMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } = useServiceStatus();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";
  const isDummy = Boolean(data?.is_dummy);

  const header = (
    <Box sx={{ textAlign: "center", mb: 4 }}>
      {isDummy && <DummyBadge />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: color.gray500 }}>
          {t("hardwareAutoRefresh", { time: lastUpdated })}
        </Typography>
        <Tooltip title={t("refreshNow")}>
          <IconButton
            size="small"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["serviceStatus"] })
            }
            sx={{ color: color.secondary, "&:hover": { color: color.accent } }}
          >
            <RefreshIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("servicesFailedToLoad", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  if (!data.available) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {header}
        <Alert severity="warning">
          {t("servicesUnavailable")}
          {data.reason ? ` (${data.reason})` : ""}
        </Alert>
      </Container>
    );
  }

  const services = data.services ?? [];
  const runningCount = services.filter((s) => s.state === "running").length;
  const totalMemUsed = services.reduce(
    (sum, s) => sum + (s.memory?.used_bytes ?? 0),
    0,
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {header}

      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          justifyContent: "center",
          flexWrap: "wrap",
          mb: 4,
        }}
      >
        <StatCard
          title={t("servicesTotal")}
          value={services.length}
          icon={<NorthIcon />}
          accentColor={color.secondary}
        />
        <StatCard
          title={t("servicesRunning")}
          value={`${runningCount} / ${services.length}`}
          icon={<SouthIcon />}
          accentColor={
            runningCount === services.length ? color.green700 : color.orange500
          }
        />
        <StatCard
          title={t("servicesMemoryTotal")}
          value={formatBytes(totalMemUsed)}
          icon={<NorthIcon />}
          accentColor={color.blue700}
        />
      </Box>

      {services.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Typography sx={{ color: color.gray400 }}>
            {t("servicesNone")}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {services.map((service) => (
            <ServiceCard key={service.name} service={service} />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default DockerServicesMonitoring;
